import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaTimes, FaCheck } from 'react-icons/fa';
import { useKitchenCart } from '../context/KitchenCartContext';
import { submitKitchenOrder } from '../services/kitchenApi';
import { getScreenSeat, clearScreenSeat } from '../utils/sessionManager';
import KitchenHeader from '../components/KitchenHeader';
import Footer from '../components/Footer';

const KitchenCheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, totalPrice, clearCart } = useKitchenCart();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mpesaNumber: '',
    screenSeat: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0) {
      // Check sessionStorage for cart data
      const savedCart = sessionStorage.getItem('kitchenCartData');
      if (!savedCart) {
        navigate('/kitchen');
      }
    }
  }, [cartItems, navigate]);

  // Load screen and seat from session (from QR code URL params)
  useEffect(() => {
    const screenSeatData = getScreenSeat();
    if (screenSeatData) {
      setFormData(prev => ({
        ...prev,
        screenSeat: `${screenSeatData.screen} - ${screenSeatData.seat}`
      }));
    }
  }, []);

  // Format price
  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'KES'
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Validate mpesa number
    if (!formData.mpesaNumber.trim()) {
      newErrors.mpesaNumber = 'M-Pesa number is required';
    } else if (!/^(07|01)\d{8}$/.test(formData.mpesaNumber)) {
      newErrors.mpesaNumber = 'Please enter a valid 10-digit M-Pesa number starting with 07 or 01';
    }
    
    // Validate screen and seat
    if (!formData.screenSeat.trim()) {
      newErrors.screenSeat = 'Screen and seat number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Show confirmation modal
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setShowConfirmModal(true);
  };
  
  // Handle actual order submission after confirmation
  const handleConfirmedSubmit = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setSubmitError('');
    
    try {
      // Prepare order data
      const orderData = {
        items: cartItems.map(item => ({
          uuid: item.uuid,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.price
        })),
        customer: {
          name: formData.name,
          email: formData.email,
          mpesa_number: formData.mpesaNumber,
          screen_seat: formData.screenSeat,
          notes: formData.notes
        },
        total_amount: totalPrice
      };
      
      const response = await submitKitchenOrder(orderData);
      
      if (response && (response.success || response.payment_status === 201)) {
        // Clear cart and session data
        clearCart();
        sessionStorage.removeItem('kitchenCartData');
        clearScreenSeat();
        
        // Show success message
        window.alert(`SUCCESS: ${response.message || 'Order placed successfully!'}

Check your phone for the M-PESA prompt.`);
        
        // Redirect to kitchen menu
        navigate('/kitchen');
      } else {
        setSubmitError(response?.message || 'Failed to place order. Please try again.');
      }
    } catch (error) {
      setSubmitError(error.message || 'An error occurred while placing your order');
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel confirmation
  const handleCancelConfirmation = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      <KitchenHeader searchQuery={''} setSearchQuery={() => {}} />
      <div className="payment-page">
        <div className="payment-container">
          <h1>Checkout - Kitchen & Bar</h1>
          
          {/* Order Summary */}
          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="order-items">
              {cartItems.map(item => (
                <div key={item.uuid} className="order-item">
                  <span className="item-name">{item.name} x {item.quantity}</span>
                  <span className="item-price">{formatPrice(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="order-total">
              <strong>Total:</strong>
              <strong>{formatPrice(totalPrice)}</strong>
            </div>
          </div>
          
          {/* Confirmation Modal */}
          {showConfirmModal && (
            <div className="confirmation-modal-overlay">
              <div className="confirmation-modal">
                <div className="confirmation-modal-header">
                  <h2>Confirm Order</h2>
                  <button 
                    className="close-modal-btn" 
                    onClick={handleCancelConfirmation}
                  >
                    <FaTimes />
                  </button>
                </div>
                
                <div className="confirmation-modal-body">
                  <p>Please verify your order details:</p>
                  
                  <div className="confirmation-details">
                    <div className="confirmation-item">
                      <span className="label">Name:</span>
                      <span className="value">{formData.name}</span>
                    </div>
                    
                    <div className="confirmation-item">
                      <span className="label">Screen and Seat:</span>
                      <span className="value">{formData.screenSeat}</span>
                    </div>
                    
                    <div className="confirmation-item">
                      <span className="label">Email:</span>
                      <span className="value">{formData.email}</span>
                    </div>
                    
                    <div className="confirmation-item">
                      <span className="label">M-Pesa Number:</span>
                      <span className="value">{formData.mpesaNumber}</span>
                    </div>
                    
                    <div className="confirmation-item">
                      <span className="label">Total Amount:</span>
                      <span className="value">{formatPrice(totalPrice)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="confirmation-modal-footer">
                  <button 
                    className="cancel-btn" 
                    onClick={handleCancelConfirmation}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="confirm-btn" 
                    onClick={handleConfirmedSubmit}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="spinner" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCheck /> Confirm & Pay
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <div className="payment-form-container">
            <form onSubmit={handleFormSubmit} className="payment-form">
              {submitError && (
                <div className="form-error">
                  <p>{submitError}</p>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  disabled={loading}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="screenSeat">Screen and Seat Number</label>
                <input
                  type="text"
                  id="screenSeat"
                  name="screenSeat"
                  value={formData.screenSeat}
                  onChange={handleChange}
                  placeholder="e.g., screen5 - a4"
                  disabled={loading}
                />
                {errors.screenSeat && <span className="error-message">{errors.screenSeat}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  disabled={loading}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="mpesaNumber">M-Pesa Number</label>
                <input
                  type="text"
                  id="mpesaNumber"
                  name="mpesaNumber"
                  value={formData.mpesaNumber}
                  onChange={handleChange}
                  placeholder="Enter your M-Pesa number (e.g., 0712345678)"
                  disabled={loading}
                />
                {errors.mpesaNumber && <span className="error-message">{errors.mpesaNumber}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="notes">Special Instructions (Optional)</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any special requests or dietary requirements"
                  disabled={loading}
                  rows={3}
                />
              </div>
              
              <button 
                type="submit" 
                className="submit-payment-btn"
                disabled={loading || cartItems.length === 0}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Processing...
                  </>
                ) : (
                  `Pay ${formatPrice(totalPrice)}`
                )}
              </button>
              
              <button 
                type="button" 
                className="back-to-menu-btn"
                onClick={() => navigate('/kitchen')}
                disabled={loading}
              >
                Back to Menu
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default KitchenCheckoutPage;
